import React from 'react';
import { quality } from '@cloudinary/url-gen/actions/delivery';
import { ArticleIntroHeader, ArticleMetaProps, MediaCaptionProps } from '@naf-simba/sections-headers';
import { DocumentTypeLabel } from '@appTypes/testType';
import { useCloudinary } from '../../hooks/Cloudinary/useCloudinary';
import { DynamicVideoPlayer } from '../video-player/DynamicVideoPlayer';
import ResponsiveImage from '../image/ResponsiveImage';
import { YoutubeEmbed } from '../YoutubeEmbed';
import { createNorwegianDateString } from '../../utils/createNorwegianDateString';
import { StandardArticleHeaderProps } from './Types';

/**
 * Standard (non-feature) article header. Thin adapter that maps app data onto the
 * Simba `ArticleIntroHeader`: picks the media element, and builds the structured
 * `meta` (byline) and `caption` props. Locale/formatting stays here.
 */
export const StandardArticleHeader = ({
  name,
  image,
  ingress,
  runningHead,
  video,
  youtubeVideo,
  userAccessibility,
  meta,
  metaDisplay,
  documentType,
}: StandardArticleHeaderProps) => {
  const { getVideo } = useCloudinary();
  const videoObject = getVideo(video?.publicId);
  const videoUrl = videoObject ? videoObject.delivery(quality('auto')).toURL() : video?.srcSet?.mp4;

  // Single media node: video → youtube → image (matches the legacy precedence).
  let media: React.ReactNode;
  if (video && videoUrl) {
    media = (
      <DynamicVideoPlayer
        video={video}
        isAccessibleForUser={userAccessibility && userAccessibility.isAccessibleForUser}
        videoPlayerOptions={{}}
        aria-labelledby={video.publicId}
        showPoster={false}
      />
    );
  } else if (youtubeVideo) {
    media = <YoutubeEmbed embed={youtubeVideo} />;
  } else if (image?.publicId) {
    media = <ResponsiveImage imageId={image.publicId} altText={image.altText || image.caption} aspectRatio="16:9" />;
  }

  // Byline — honor metaDisplay suppression + documentType rules (locale stays app-side).
  const showMeta = metaDisplay?.metaPosition !== 'bottom';
  const articleMeta: ArticleMetaProps | undefined = showMeta
    ? {
        author:
          meta?.editor && !metaDisplay?.hideEditor && documentType !== DocumentTypeLabel.nafnoNewsArticle
            ? { name: meta.editor.name, position: meta.editor.position }
            : undefined,
        publishedAt:
          meta?.publishAt && !metaDisplay?.hidePublicationDate
            ? createNorwegianDateString(new Date(meta.publishAt))
            : undefined,
        publishedLabel: 'Publisert:',
        updatedAt:
          meta?.updatedAt && !metaDisplay?.hideUpdateDate
            ? createNorwegianDateString(new Date(meta.updatedAt))
            : undefined,
        updatedLabel: 'Sist oppdatert:',
      }
    : undefined;

  // Caption — video caption, else image photographer/copyright/caption.
  let caption: MediaCaptionProps | undefined;
  if (video?.caption) {
    caption = { caption: video.caption, id: video.publicId };
  } else if (image && (image.caption || image.photographer || image.copyright)) {
    caption = {
      caption: image.caption,
      photographer: image.photographer,
      copyright: image.copyright,
      photoLabel: 'Foto:',
      id: image.publicId,
    };
  }

  return (
    <ArticleIntroHeader
      runningHead={runningHead}
      title={name}
      ingress={ingress}
      meta={articleMeta}
      metaPlacement={documentType === DocumentTypeLabel.legalDocument ? 'aboveIngress' : 'belowIngress'}
      imageOrVideo={media}
      caption={caption}
      nafBranded
    />
  );
};
